import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTabs, DialogTabsContent, DialogTabsList, DialogTabsTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Trash2, Plus, Loader2, Check, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { listarFormasPagamento, criarFormaPagamento, deletarFormaPagamento } from '@/integrations/supabase/formas-pagamento';
import { listarContasBancarias, criarContaBancaria, deletarContaBancaria, atualizarContaBancaria } from '@/integrations/supabase/contas-bancarias';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const TIPOS_FORMA = ['dinheiro', 'cartao_debito', 'cartao_credito', 'pix', 'boleto', 'cheque', 'outro'];
const TIPOS_CONTA = ['caixa', 'banco', 'carteira', 'marketplace', 'outro'];

// Componente de Ajuda
function DialogAjudaConfiguracao({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Dialog open={isOpen} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Como usar: Configurar Contas e Formas de Pagamento</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4 space-y-2">
            <h3 className="font-semibold">💳 Formas de Pagamento</h3>
            <p className="text-sm text-gray-700">
              Use esta seção para definir como você recebe pagamentos dos clientes:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 text-gray-600">
              <li><strong>PIX:</strong> Transferências instantâneas</li>
              <li><strong>Cartão Débito:</strong> Pagamentos em cartão de débito</li>
              <li><strong>Cartão Crédito:</strong> Pagamentos em cartão de crédito</li>
              <li><strong>Dinheiro:</strong> Dinheiro físico em caixa</li>
              <li><strong>Boleto:</strong> Boletos bancários</li>
              <li><strong>Cheque:</strong> Cheques recebidos</li>
              <li><strong>Outro:</strong> Outros métodos</li>
            </ul>
            <p className="text-sm text-gray-700 mt-2">
              <strong>Como usar:</strong> Preencha o nome (ex: "PIX Pessoal"), escolha o tipo e clique em "Adicionar Forma".
            </p>
          </div>

          <div className="border-l-4 border-green-500 pl-4 space-y-2">
            <h3 className="font-semibold">🏦 Contas Bancárias</h3>
            <p className="text-sm text-gray-700">
              Use esta seção para registrar onde o dinheiro é armazenado:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 text-gray-600">
              <li><strong>Caixa Diário:</strong> Dinheiro físico na loja (gaveta, cofre)</li>
              <li><strong>Banco:</strong> Conta bancária (Itaú, Bradesco, Caixa, etc.)</li>
              <li><strong>Carteira:</strong> Dinheiro em sua carteira pessoal</li>
              <li><strong>Marketplace:</strong> Mercado Pago, PayPal, Pix dinâmico</li>
              <li><strong>Outro:</strong> Outras contas customizadas</li>
            </ul>
            <p className="text-sm text-gray-700 mt-2">
              <strong>Como usar:</strong>
            </p>
            <ol className="list-decimal list-inside text-sm space-y-1 text-gray-600">
              <li>Preencha o <strong>Nome</strong> (ex: "Caixa Loja")</li>
              <li>Escolha o <strong>Tipo</strong></li>
              <li>Se for banco/marketplace, indique a instituição (ex: "Itaú")</li>
              <li>Digite o <strong>Saldo Inicial</strong> (quanto você tinha quando começou a usar o sistema)</li>
              <li>Defina a <strong>Data do Saldo</strong></li>
              <li>Clique em "Adicionar Conta"</li>
            </ol>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">💡 Dica Importante</h4>
            <p className="text-sm text-gray-700">
              O saldo inicial é o ponto de partida para rastreamento. Se você tem <strong>R$ 5.000 em caixa</strong> hoje, defina o saldo inicial como 5.000 e a data como hoje. A partir daqui, o sistema vai acompanhar automaticamente todas as entradas e saídas!
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">🎯 Exemplo Prático</h4>
            <p className="text-sm text-gray-700 mb-2">Para uma ateliê típica:</p>
            <ul className="list-disc list-inside text-sm space-y-1 text-gray-600">
              <li><strong>Formas:</strong> PIX, Dinheiro, Cartão Débito, Cartão Crédito</li>
              <li><strong>Contas:</strong> Caixa Loja, Banco Itaú, Mercado Pago</li>
            </ul>
            <p className="text-sm text-gray-700 mt-2">
              Assim você saberá: quanto foi recebido por PIX, quanto em dinheiro, quanto em cartão débito, quanto em cartão crédito, e onde cada valor está guardado!
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Entendi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LABELS_TIPO_FORMA: Record<string, string> = {
  dinheiro: 'Dinheiro',
  cartao_debito: 'Cartão Débito',
  cartao_credito: 'Cartão Crédito',
  pix: 'PIX',
  boleto: 'Boleto',
  cheque: 'Cheque',
  outro: 'Outro'
};

const LABELS_TIPO_CONTA: Record<string, string> = {
  caixa: 'Caixa Diário',
  banco: 'Banco',
  carteira: 'Carteira',
  marketplace: 'Marketplace',
  outro: 'Outro'
};

export function DialogConfigurarContasEFormas({ isOpen, onClose }: DialogProps) {
  // Estados gerais
  const [isAjudaOpen, setIsAjudaOpen] = useState(false);

  // Formas de Pagamento
  const [novaFormaName, setNovaFormaName] = useState('');
  const [novaFormaTipo, setNovaFormaTipo] = useState('dinheiro');
  const [novaFormaTaxa, setNovaFormaTaxa] = useState('');
  const [novaFormaDescricao, setNovaFormaDescricao] = useState('');
  const [novaFormaObs, setNovaFormaObs] = useState('');
  const [novaFormaPrazo, setNovaFormaPrazo] = useState('');
  const [novaFormaImpostosInclusos, setNovaFormaImpostosInclusos] = useState(false);
  const [novaFormaResponsavel, setNovaFormaResponsavel] = useState('');
  const [isAddingForma, setIsAddingForma] = useState(false);

  // Contas Bancárias
  const [novaContaName, setNovaContaName] = useState('');
  const [novaContaTipo, setNovaContaTipo] = useState('caixa');
  const [novaContaBanco, setNovaContaBanco] = useState('');
  const [novaContaSaldoInicial, setNovaContaSaldoInicial] = useState('0');
  const [novaContaData, setNovaContaData] = useState(new Date().toISOString().split('T')[0]);
  const [isAddingConta, setIsAddingConta] = useState(false);
  const [editandoContaId, setEditandoContaId] = useState<string | null>(null);

  // Queries
  const { data: formasPagamento = [], refetch: refetchFormas } = useQuery({
    queryKey: ['formas_pagamento'],
    queryFn: listarFormasPagamento
  });

  const { data: contasBancarias = [], refetch: refetchContas } = useQuery({
    queryKey: ['contas_bancarias'],
    queryFn: listarContasBancarias
  });

  // Handlers Formas
  const handleAddForma = async () => {
    if (!novaFormaName.trim()) {
      toast.error('Nome da forma de pagamento obrigatório');
      return;
    }

    setIsAddingForma(true);
    try {
      const result = await criarFormaPagamento({
        nome: novaFormaName,
        tipo: novaFormaTipo as any,
        ativa: true,
        taxa_padrao: novaFormaTaxa ? parseFloat(novaFormaTaxa) : undefined,
        descricao: novaFormaDescricao || undefined,
        obs_forma_pagamento: novaFormaObs || undefined,
        prazo_entrega: novaFormaPrazo || undefined,
        impostos_inclusos: novaFormaImpostosInclusos,
        responsavel: novaFormaResponsavel || undefined
      });

      if (result.ok) {
        toast.success(`Forma de pagamento "${novaFormaName}" criada!`);
        setNovaFormaName('');
        setNovaFormaTipo('dinheiro');
        setNovaFormaTaxa('');
        setNovaFormaDescricao('');
        setNovaFormaObs('');
        setNovaFormaPrazo('');
        setNovaFormaImpostosInclusos(false);
        setNovaFormaResponsavel('');
        refetchFormas();
      } else {
        toast.error(result.error || 'Erro ao criar forma de pagamento');
      }
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao criar forma de pagamento');
    } finally {
      setIsAddingForma(false);
    }
  };

  const handleDeleteForma = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar?')) return;

    try {
      const result = await deletarFormaPagamento(id);
      if (result.ok) {
        toast.success('Forma de pagamento deletada');
        refetchFormas();
      } else {
        toast.error(result.error || 'Erro ao deletar');
      }
    } catch (erro: any) {
      toast.error(erro.message);
    }
  };

  // Handlers Contas
  const handleAddConta = async () => {
    if (!novaContaName.trim()) {
      toast.error('Nome da conta obrigatório');
      return;
    }

    setIsAddingConta(true);
    try {
      if (editandoContaId) {
        // Modo edição
        const result = await atualizarContaBancaria(editandoContaId, {
          nome: novaContaName,
          tipo: novaContaTipo as any,
          banco: novaContaBanco || undefined,
          saldo_inicial: parseFloat(novaContaSaldoInicial) || 0,
          data_saldo_inicial: novaContaData
        });

        if (result.ok) {
          toast.success(`Conta "${novaContaName}" atualizada!`);
          setNovaContaName('');
          setNovaContaTipo('caixa');
          setNovaContaBanco('');
          setNovaContaSaldoInicial('0');
          setNovaContaData(new Date().toISOString().split('T')[0]);
          setEditandoContaId(null);
          refetchContas();
        } else {
          toast.error(result.error || 'Erro ao atualizar conta');
        }
      } else {
        // Modo criar
        const result = await criarContaBancaria({
          nome: novaContaName,
          tipo: novaContaTipo as any,
          banco: novaContaBanco || undefined,
          saldo_inicial: parseFloat(novaContaSaldoInicial) || 0,
          data_saldo_inicial: novaContaData,
          ativa: true
        });

        if (result.ok) {
          toast.success(`Conta "${novaContaName}" criada!`);
          setNovaContaName('');
          setNovaContaTipo('caixa');
          setNovaContaBanco('');
          setNovaContaSaldoInicial('0');
          setNovaContaData(new Date().toISOString().split('T')[0]);
          refetchContas();
        } else {
          toast.error(result.error || 'Erro ao criar conta');
        }
      }
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao processar conta');
    } finally {
      setIsAddingConta(false);
    }
  };

  const handleEditarConta = (conta: any) => {
    setEditandoContaId(conta.id);
    setNovaContaName(conta.nome);
    setNovaContaTipo(conta.tipo);
    setNovaContaBanco(conta.banco || '');
    setNovaContaSaldoInicial(conta.saldo_inicial.toString());
    setNovaContaData(conta.data_saldo_inicial);
  };

  const handleCancelarEdicao = () => {
    setEditandoContaId(null);
    setNovaContaName('');
    setNovaContaTipo('caixa');
    setNovaContaBanco('');
    setNovaContaSaldoInicial('0');
    setNovaContaData(new Date().toISOString().split('T')[0]);
  };

  const handleDeleteConta = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar?')) return;

    try {
      const result = await deletarContaBancaria(id);
      if (result.ok) {
        toast.success('Conta deletada');
        refetchContas();
      } else {
        toast.error(result.error || 'Erro ao deletar');
      }
    } catch (erro: any) {
      toast.error(erro.message);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(aberto) => !aberto && onClose()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between w-full">
              <DialogTitle>Configurar Contas e Formas de Pagamento</DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAjudaOpen(true)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* FORMAS DE PAGAMENTO */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">💳 Formas de Pagamento</h3>

              {/* Lista */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {formasPagamento.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Nenhuma forma adicionada</p>
                ) : (
                  formasPagamento.map((forma) => (
                    <Card key={forma.id} className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{forma.nome}</p>
                          <p className="text-xs text-gray-500">{LABELS_TIPO_FORMA[forma.tipo]}</p>
                          {forma.taxa_padrao && (
                            <p className="text-xs text-blue-600 font-medium mt-1">💰 Taxa: {forma.taxa_padrao.toFixed(2)}%</p>
                          )}
                          {forma.descricao && (
                            <p className="text-xs text-gray-600 mt-1">📝 {forma.descricao}</p>
                          )}
                          {forma.prazo_entrega && (
                            <p className="text-xs text-gray-600">📅 Prazo: {forma.prazo_entrega}</p>
                          )}
                          {forma.impostos_inclusos && (
                            <p className="text-xs text-green-600 font-medium">✅ Impostos inclusos</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => forma.id && handleDeleteForma(forma.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))
                )}
              </div>

              {/* Adicionar */}
              <div className="border-t pt-3 space-y-2">
                <Label htmlFor="nova-forma-nome" className="text-xs">Nome</Label>
                <Input
                  id="nova-forma-nome"
                  placeholder="Ex: PIX Pessoal"
                  value={novaFormaName}
                  onChange={(e) => setNovaFormaName(e.target.value)}
                  size="sm"
                />

                <Label htmlFor="nova-forma-tipo" className="text-xs">Tipo</Label>
                <Select value={novaFormaTipo} onValueChange={setNovaFormaTipo}>
                  <SelectTrigger id="nova-forma-tipo" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_FORMA.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>
                        {LABELS_TIPO_FORMA[tipo]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(novaFormaTipo === 'cartao_credito' || novaFormaTipo === 'cartao_debito') && (
                  <>
                    <Label htmlFor="nova-forma-taxa" className="text-xs">Taxa Padrão (%)</Label>
                    <Input
                      id="nova-forma-taxa"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="Ex: 2.99"
                      value={novaFormaTaxa}
                      onChange={(e) => setNovaFormaTaxa(e.target.value)}
                      size="sm"
                    />
                  </>
                )}

                <Label htmlFor="nova-forma-descricao" className="text-xs">Descrição</Label>
                <Input
                  id="nova-forma-descricao"
                  placeholder="Ex: 50% ENTRADA 50% RETIRADA"
                  value={novaFormaDescricao}
                  onChange={(e) => setNovaFormaDescricao(e.target.value)}
                  size="sm"
                />

                <Label htmlFor="nova-forma-obs" className="text-xs">Observações</Label>
                <Input
                  id="nova-forma-obs"
                  placeholder="Ex: Confirmar com cliente antes"
                  value={novaFormaObs}
                  onChange={(e) => setNovaFormaObs(e.target.value)}
                  size="sm"
                />

                <Label htmlFor="nova-forma-prazo" className="text-xs">Prazo de Entrega</Label>
                <Input
                  id="nova-forma-prazo"
                  placeholder="Ex: A combinar, 30 dias, 15 dias"
                  value={novaFormaPrazo}
                  onChange={(e) => setNovaFormaPrazo(e.target.value)}
                  size="sm"
                />

                <Label htmlFor="nova-forma-responsavel" className="text-xs">Responsável</Label>
                <Input
                  id="nova-forma-responsavel"
                  placeholder="Ex: João Silva"
                  value={novaFormaResponsavel}
                  onChange={(e) => setNovaFormaResponsavel(e.target.value)}
                  size="sm"
                />

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="nova-forma-impostos"
                    checked={novaFormaImpostosInclusos}
                    onChange={(e) => setNovaFormaImpostosInclusos(e.target.checked)}
                  />
                  <Label htmlFor="nova-forma-impostos" className="text-xs cursor-pointer">
                    Impostos inclusos?
                  </Label>
                </div>

                <Button
                  onClick={handleAddForma}
                  disabled={isAddingForma}
                  className="w-full mt-2"
                  size="sm"
                >
                  {isAddingForma ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Adicionar Forma
                </Button>
              </div>
            </div>

            {/* CONTAS BANCÁRIAS */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">🏦 Contas Bancárias</h3>

              {/* Lista */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {contasBancarias.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Nenhuma conta adicionada</p>
                ) : (
                  contasBancarias.map((conta) => (
                    <Card key={conta.id} className={`p-3 ${editandoContaId === conta.id ? 'border-purple-500 bg-purple-50' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{conta.nome}</p>
                          <p className="text-xs text-gray-500">{LABELS_TIPO_CONTA[conta.tipo]}</p>
                          {conta.banco && <p className="text-xs text-gray-500">{conta.banco}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditarConta(conta)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Editar conta"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => conta.id && handleDeleteConta(conta.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs bg-gray-50 p-2 rounded">
                        <p className="font-semibold">Saldo Inicial: {conta.saldo_inicial.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                      </div>
                    </Card>
                  ))
                )}
              </div>

              {/* Adicionar */}
              <div className="border-t pt-3 space-y-2">
                <Label htmlFor="nova-conta-nome" className="text-xs">Nome</Label>
                <Input
                  id="nova-conta-nome"
                  placeholder="Ex: Caixa Looja"
                  value={novaContaName}
                  onChange={(e) => setNovaContaName(e.target.value)}
                  size="sm"
                />

                <Label htmlFor="nova-conta-tipo" className="text-xs">Tipo</Label>
                <Select value={novaContaTipo} onValueChange={setNovaContaTipo}>
                  <SelectTrigger id="nova-conta-tipo" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONTA.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>
                        {LABELS_TIPO_CONTA[tipo]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(novaContaTipo === 'banco' || novaContaTipo === 'marketplace') && (
                  <>
                    <Label htmlFor="nova-conta-banco" className="text-xs">Banco/Instituição</Label>
                    <Input
                      id="nova-conta-banco"
                      placeholder="Ex: Itaú, Mercado Pago"
                      value={novaContaBanco}
                      onChange={(e) => setNovaContaBanco(e.target.value)}
                      size="sm"
                    />
                  </>
                )}

                <Label htmlFor="nova-conta-saldo" className="text-xs">Saldo Inicial (R$)</Label>
                <Input
                  id="nova-conta-saldo"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={novaContaSaldoInicial}
                  onChange={(e) => setNovaContaSaldoInicial(e.target.value)}
                  size="sm"
                />

                <Label htmlFor="nova-conta-data" className="text-xs">Data do Saldo</Label>
                <Input
                  id="nova-conta-data"
                  type="date"
                  value={novaContaData}
                  onChange={(e) => setNovaContaData(e.target.value)}
                  size="sm"
                />

                <div className="flex gap-2 mt-2">
                  <Button
                    onClick={handleAddConta}
                    disabled={isAddingConta}
                    className="flex-1"
                    size="sm"
                  >
                    {isAddingConta ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : editandoContaId ? (
                      <Check className="w-4 h-4 mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    {editandoContaId ? 'Atualizar Conta' : 'Adicionar Conta'}
                  </Button>
                  {editandoContaId && (
                    <Button
                      onClick={handleCancelarEdicao}
                      variant="outline"
                      size="sm"
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Dialog de Ajuda */}
    <DialogAjudaConfiguracao
      isOpen={isAjudaOpen}
      onClose={() => setIsAjudaOpen(false)}
    />
    </>
  );
}
